/**
 * Browser notifications that are noise rather than defects, so Bugsnag never
 * reports them.
 *
 * Keep this list short and specific. Anything added here becomes invisible in
 * production, so it must be something genuinely harmless — not a real fault that
 * happens to be noisy.
 */
const IGNORED_ERROR_PATTERNS: readonly RegExp[] = [
  /**
   * "ResizeObserver loop completed with undelivered notifications." and the
   * older "ResizeObserver loop limit exceeded."
   *
   * Emitted when a ResizeObserver callback itself changes layout, so the
   * observation does not settle within one frame. The browser delivers the
   * remaining notifications on the next frame — nothing is dropped and nothing
   * breaks. It reaches Bugsnag as an *unhandled* error purely because the spec
   * surfaces it through `window.onerror`, with no stack frames.
   *
   * Ours originates in Angular Material's slider: `_onResize` reads geometry and
   * then writes width/left/right onto the track and range inputs (see
   * `_observeHostResize` in @angular/material slider). Observed on the Applied
   * tab's filters dialog.
   */
  /^ResizeObserver loop/i,
];

/** One error entry as Bugsnag reports it — only the message is inspected here. */
export interface ReportedError {
  errorMessage: string;
}

/**
 * Whether a Bugsnag event should be sent. Used as the `onError` callback, which
 * discards the event when it returns false.
 */
export function shouldReportToBugsnag(errors: readonly ReportedError[]): boolean {
  return !errors.some((error) => IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(error.errorMessage ?? '')));
}
